import { Box, Typography, Grid, IconButton, Button, Tooltip } from "@material-ui/core";
import ToolkitTable from "components/Cards/Tables/ToolkitTable";
import LoadingData from "components/Loading/LoadingData";
import { columnTextField } from "utils/tables-utils";
import themeColorsDesingSystem from "assets/theme/ds-colors";
import EditIcon from "@mui/icons-material/Edit";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";

const STATUS_LABEL = {
  ATIVO: "Ativo",
  EM_AVALIACAO: "Em Avaliação",
  PENDENTE: "Pendente",
  INATIVO: "Inativo",
};

const STATUS_STYLE = {
  ATIVO: { bg: themeColorsDesingSystem.green.backgroundActive, color: themeColorsDesingSystem.green.main },
  EM_AVALIACAO: { bg: themeColorsDesingSystem.yellow.background, color: themeColorsDesingSystem.yellow.main },
  PENDENTE: { bg: themeColorsDesingSystem.yellow.background, color: themeColorsDesingSystem.yellow.main },
  INATIVO: { bg: "#f5f5f5", color: themeColorsDesingSystem.gray?.main || "#616161" },
};

const STATUS_ICONS = {
  ATIVO: <CheckCircleIcon fontSize="small" />,
  EM_AVALIACAO: <BlockIcon fontSize="small" />,
  PENDENTE: <BlockIcon fontSize="small" />,
  INATIVO: <BlockIcon fontSize="small" />,
};

export default function TableSuppliers({
  data,
  loading,
  readOnly = false,
  onResearch,
  onAddSupplier,
  onEdit,
  onInativarReativar,
  onViewDetails,
}) {
  const baseColumns = [
    columnTextField({
      dataField: "Nome",
      text: "Nome Fantasia",
      align: "center",
    }),
    columnTextField({
      dataField: "Categoria",
      text: "Categoria",
      align: "center",
    }),
    columnTextField({
      dataField: "Contato",
      text: "Contato Principal",
      align: "center",
      formatter: (_, row) => (
        <Box>
          <Typography variant="paragraph">{row.Contato}</Typography>
          <Typography variant="body1" style={{ color: themeColorsDesingSystem.gray.main }}>
            {row.Slug || ""}
          </Typography>
        </Box>
      ),
    }),
    columnTextField({
      dataField: "TelefoneEmail",
      text: "Telefone / E-mail",
      align: "center",
      formatter: (_, row) => (
        <Box>
          <Typography variant="paragraph">{row.Telefone || "-"}</Typography>
          <Typography variant="body1" style={{ color: themeColorsDesingSystem.gray.main }}>
            {row.Email || "-"}
          </Typography>
        </Box>
      ),
    }),
    columnTextField({
      dataField: "Status",
      text: "Status de Homologação",
      align: "center",
      formatter: (_, row) => {
        const s = STATUS_STYLE[row.Status] || STATUS_STYLE.EM_AVALIACAO;
        return (
          <Box
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.25rem",
              padding: "0.25rem 0.5rem",
              borderRadius: "0.25rem",
              backgroundColor: s.bg,
              color: s.color,
            }}
          >
            {STATUS_ICONS[row.Status]} {STATUS_LABEL[row.Status] || row.Status}
          </Box>
        );
      },
    }),
  ];

  const acoesColumn = columnTextField({
    dataField: "Acoes",
    text: "Ações",
    align: "center",
    formatter: (_, row) => (
      <Box style={{ display: "flex", gap: 4, justifyContent: "center", flexWrap: "wrap" }}>
        <Tooltip title="Editar">
          <IconButton size="small" onClick={() => onEdit?.(row)} aria-label="Editar">
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={row.Status === "INATIVO" ? "Reativar" : "Inativar"}>
          <IconButton
            size="small"
            onClick={() => onInativarReativar?.(row)}
            aria-label={row.Status === "INATIVO" ? "Reativar" : "Inativar"}
          >
            {row.Status === "INATIVO" ? (
              <CheckCircleIcon fontSize="small" />
            ) : (
              <BlockIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Box>
    ),
  });

  const detalhesColumn = columnTextField({
    dataField: "Detalhes",
    text: "Detalhes",
    align: "center",
    formatter: (_, row) => (
      <Tooltip title="Ver detalhes">
        <IconButton size="small" onClick={() => onViewDetails?.(row)} aria-label="Ver detalhes">
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    ),
  });

  const columnModel = readOnly
    ? [...baseColumns, detalhesColumn]
    : [...baseColumns, acoesColumn];

  return (
    <div>
      {loading ? (
        <LoadingData searching={true} label="Buscando fornecedores..." />
      ) : (
        data.length > 0 ? (
          <ToolkitTable
            keyField="IdFornecedor"
            dataTable={data}
            columnModel={columnModel}
            notFoundTitle="Sua pesquisa não encontrou nenhum resultado correspondente."
            CustomHeader={() => (
              <Grid
                container
                justify="space-between"
                spacing={2}
                style={{ padding: "0 20px 20px 20px" }}
              >
                <Grid item>
                  <Grid container spacing={2} alignItems="center">
                    <Typography>Fornecedores{readOnly ? " (somente consulta)" : ""}</Typography>
                    {onResearch && (
                      <Tooltip title="Atualizar">
                        <IconButton size="small" onClick={onResearch} aria-label="Atualizar">
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Grid>
                </Grid>
                {!readOnly && (
                  <Button variant="contained" disabled={loading} onClick={onAddSupplier}>
                    <AddIcon /> Novo Fornecedor
                  </Button>
                )}
              </Grid>
            )}
            pagination={{
              options: [25, 50, 100],
              defaultOption: 25,
              showTotal: true,
            }}
          />
        ) : (
          <Box
            style={{
              display: "flex",
              justifyContent: readOnly ? "center" : "flex-end",
              marginTop: 20,
            }}
          >
            {readOnly ? (
              <Typography color="textSecondary">Nenhum fornecedor encontrado.</Typography>
            ) : (
              <Button color="default" variant="contained" onClick={onAddSupplier}>
                <AddIcon /> Novo Fornecedor
              </Button>
            )}
          </Box>
        )
      )}
    </div>
  );
}
